"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateUtil = void 0;
class DateUtil {
    static readableDateOptions = {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    };
    static daysInTheFuture(days) {
        const date = new Date();
        date.setDate(date.getDate() + days);
        return date;
    }
    static convertToDisplayDate(d, timezone) {
        const date = new Date(d);
        const options = {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
        };
        if (timezone) {
            options.timeZone = timezone;
        }
        const formatter = new Intl.DateTimeFormat('en-US', options);
        return formatter.format(date);
    }
    static convertToDisplayTime(d, timezone, includeSeconds = false) {
        const date = new Date(d);
        const options = {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        };
        if (includeSeconds) {
            options.second = '2-digit';
        }
        if (timezone) {
            options.timeZone = timezone;
        }
        const formatter = new Intl.DateTimeFormat('en-US', options);
        return formatter.format(date);
    }
    static adjustDateToUtcTimezone(date) {
        const offsetMinutes = date.getTimezoneOffset();
        const adjustedTime = date.getTime() + offsetMinutes * 60 * 1000;
        return new Date(adjustedTime);
    }
    static convertToDisplayDateAndTime(d, timezone) {
        const date = new Date(d);
        const dateFormatOptions = {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
        };
        const timeFormatOptions = {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        };
        if (timezone) {
            dateFormatOptions.timeZone = timezone;
            timeFormatOptions.timeZone = timezone;
        }
        const dateFormatter = new Intl.DateTimeFormat('en-US', dateFormatOptions);
        const timeFormatter = new Intl.DateTimeFormat('en-US', timeFormatOptions);
        return `${dateFormatter.format(date)} at ${timeFormatter.format(date)}`;
    }
    static convertTimestampToFormTime(timestamp) {
        const date = new Date(timestamp);
        const hour = date.getHours();
        const minute = `0${date.getMinutes()}`.slice(-2);
        return `${hour}:${minute}`;
    }
    static convertTo12HourFormat(time24) {
        const [hourStr, minute] = time24.split(':');
        let hour = parseInt(hourStr, 10);
        const period = hour >= 12 ? 'PM' : 'AM';
        hour = hour % 12 || 12; // Convert "0" to "12"
        return `${hour}:${minute} ${period}`;
    }
    static convertFormTimeIntoDate(formTime) {
        const [hour, minute] = formTime.split(':').map((n) => parseInt(n, 10));
        const date = new Date();
        date.setHours(hour);
        date.setMinutes(minute);
        return date;
    }
    static convertTimestampToReadableDate(timestamp, timezone) {
        const date = new Date(timestamp);
        const options = {
            ...this.readableDateOptions
        };
        if (timezone) {
            options.timeZone = timezone;
        }
        return date.toLocaleDateString('en-US', options);
    }
    static setTime(date, hour, minute) {
        date.setHours(hour);
        date.setMinutes(minute);
        return date;
    }
    // generates date objects for x days in the future
    static generateDates(days) {
        const dates = [];
        for (let i = 0; i < days; i++) {
            dates.push(this.daysInTheFuture(i));
        }
        return dates;
    }
    // gets timestamp of beginning and end of month and returns them in an object { startDate: number, endDate: number }
    static getMonthStartAndEndDates(date) {
        const startDate = new Date(date.getFullYear(), date.getMonth(), 1).getTime();
        const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0).getTime();
        return { startDate, endDate };
    }
    // TODO - pick actual dates
    static getEarliestPossibleDate() {
        return 1594364400000;
    }
    static getLatestPossibleDate() {
        // July 31, 2027
        // TODO: This need to be dynamic so we dont have to hardcode the date everytime we release a new year
        return 1817010000000;
    }
    static isTodayOrPast(date) {
        const today = new Date();
        today.setHours(23, 59, 59, 0); // 11:59:59 PM (milliseconds = 0)
        return date <= today;
    }
    static isToday(timestamp) {
        const inputDate = new Date(timestamp);
        const now = new Date();
        return (inputDate.getFullYear() === now.getFullYear() &&
            inputDate.getMonth() === now.getMonth() &&
            inputDate.getDate() === now.getDate());
    }
    static isPast(date) {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // 11:59:59 PM (milliseconds = 0)
        return date <= today;
    }
    static isTodayOrFuture(date) {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // 11:59:59 PM (milliseconds = 0)
        return date >= today;
    }
    /**
     * Returns the first and last Date of the month for the given date.
     * - firstDay is at 00:00 (12:00 AM)
     * - lastDay is at 23:59 (11:59 PM)
     */
    static getMonthBoundaries(inputDate = new Date()) {
        const year = inputDate.getFullYear();
        const month = inputDate.getMonth();
        // First day: day 1 of this month at 00:00:00
        const firstDay = new Date(year, month, 1, 0, 0, 0, 0);
        // Last day: day 0 of next month gives last day of this month,
        // then set time to 23:59:00
        const lastDay = new Date(year, month + 1, 0, 23, 59, 0, 0);
        return { firstDay, lastDay };
    }
    static convertDateToTimezone(date, timeZone) {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
        const parts = formatter.formatToParts(date);
        const year = parseInt(parts.find(p => p.type === 'year')?.value || '0', 10);
        const month = parseInt(parts.find(p => p.type === 'month')?.value || '1', 10);
        const day = parseInt(parts.find(p => p.type === 'day')?.value || '1', 10);
        const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
        const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
        const second = parseInt(parts.find(p => p.type === 'second')?.value || '0', 10);
        return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
    }
}
exports.DateUtil = DateUtil;
